import chroma from "chroma-js";
import type { GlobalToken } from "antd";

// Color scheme types for reference
export type ColorSchemeType =
  | "complement"
  | "triadic"
  | "splitComplement"
  | "analogous"
  | "tetradic"
  | "square"
  | "accent"
  | "qualitative"
  | "sequential"
  | "diverging";

interface ColorSchemeOptions {
  count?: number;
  saturation?: number;
  lightness?: number;
  rotation?: number;
  spread?: number;
}

// Re-export existing types
export enum RampTheme {
  // Data Visualization (Categorical)
  classic = "classic", // Professional distinct colors (formerly tableau)
  accessible = "accessible", // Colorblind-friendly
  material = "material", // Material Design 3.0
  qualitative = "qualitative", // Pure categorical palette
  research = "research", // Academic/scientific categorical
  presentation = "presentation", // High-contrast for presentations

  // Data Visualization (Scientific & Statistical)
  scientific = "scientific", // Perceptually uniform for precise visualization
  statistical = "statistical", // Optimized for statistical charts

  // Data Visualization (Sequential)
  sequential_blue = "sequential_blue", // Professional blue progression
  sequential_multi = "sequential_multi", // Multi-hue sequential

  // Data Visualization (Diverging)
  diverging_rb = "diverging_rb", // Red-Blue for pos/neg
  diverging_bg = "diverging_bg", // Brown-Green for environment

  // Business & Finance
  financial = "financial", // Financial Times inspired
  enterprise = "enterprise", // Corporate style

  // Nature Inspired
  earth = "earth", // Earth tones
  forest = "forest", // Forest greens
  ocean = "ocean", // Ocean blues

  // UI Progressions
  neutral = "neutral", // Grayscale progression
  high_contrast = "high_contrast", // Maximum contrast
  modern = "modern", // Clean, professional progression
}

interface ChromaTheme {
  startColor: string;
  endColor: string;
  midPoints?: string[];
  colorSpace: "oklch" | "lab" | "lch";
  description: string;
}

// Enhanced theme definitions using better color science
export const RAMPS: Record<
  RampTheme,
  {
    name: string;
    description: string;
    light: ChromaTheme;
    dark: ChromaTheme;
  }
> = {
  // Data Visualization (Categorical)
  [RampTheme.classic]: {
    name: "Classic",
    description:
      "Sophisticated analytics palette with muted, professional colors",
    light: {
      startColor: "#4A5568", // slate blue-gray
      endColor: "#C53030", // muted red
      midPoints: [
        "#2F855A", // muted green
        "#D69E2E", // muted gold
        "#6B46C1", // muted purple
        "#B83280", // muted pink
        "#0987A0", // muted teal
        "#A0AEC0", // soft gray
        "#DD6B20", // muted orange
        "#4C51BF", // muted indigo
      ],
      colorSpace: "lab",
      description: "Professional muted colors for sophisticated analytics",
    },
    dark: {
      startColor: "#718096", // lighter slate for dark
      endColor: "#E53E3E", // slightly brighter red for dark
      midPoints: [
        "#48BB78", // brighter muted green
        "#ECC94B", // brighter muted gold
        "#805AD5", // brighter muted purple
        "#D53F8C", // brighter muted pink
        "#319795", // brighter muted teal
        "#CBD5E0", // lighter soft gray
        "#ED8936", // brighter muted orange
        "#667EEA", // brighter muted indigo
      ],
      colorSpace: "lab",
      description: "Enhanced muted colors for dark mode readability",
    },
  },

  [RampTheme.accessible]: {
    name: "Accessible",
    description: "Colorblind-friendly visualization palette",
    light: {
      startColor: "#006BA4",
      endColor: "#FF800E",
      midPoints: ["#595959", "#A2C8EC", "#FFBC79", "#ABABAB"],
      colorSpace: "lab",
      description: "Accessible visualization colors",
    },
    dark: {
      startColor: "#004C73",
      endColor: "#B35900",
      midPoints: ["#3D3D3D", "#71A1CB", "#CC8F47", "#787878"],
      colorSpace: "lab",
      description: "Deep accessible colors",
    },
  },

  [RampTheme.material]: {
    name: "Material",
    description: "Refined Material Design visualization palette",
    light: {
      startColor: "#6366F1", // modern indigo
      endColor: "#F87171", // soft coral
      midPoints: [
        "#06B6D4", // cyan
        "#10B981", // emerald
        "#F59E0B", // amber
        "#8B5CF6", // violet
        "#EC4899", // pink
        "#6B7280", // gray
        "#14B8A6", // teal
      ],
      colorSpace: "oklch",
      description: "Modern sophisticated material colors",
    },
    dark: {
      startColor: "#8B5CF6", // brighter violet for dark
      endColor: "#F87171", // coral remains good for dark
      midPoints: [
        "#22D3EE", // brighter cyan
        "#34D399", // brighter emerald
        "#FBBF24", // brighter amber
        "#A78BFA", // brighter violet
        "#F472B6", // brighter pink
        "#9CA3AF", // brighter gray
        "#2DD4BF", // brighter teal
      ],
      colorSpace: "oklch",
      description: "Enhanced material colors for dark mode",
    },
  },

  [RampTheme.qualitative]: {
    name: "Qualitative",
    description: "Pure categorical palette optimized for maximum distinction",
    light: {
      startColor: "#1F77B4", // tableau blue
      endColor: "#17BECF", // tableau cyan
      midPoints: [
        "#FF7F0E", // tableau orange
        "#2CA02C", // tableau green
        "#D62728", // tableau red
        "#9467BD", // tableau purple
        "#8C564B", // tableau brown
        "#E377C2", // tableau pink
        "#7F7F7F", // tableau gray
        "#BCBD22", // tableau olive
      ],
      colorSpace: "lab",
      description: "Research-backed categorical colors",
    },
    dark: {
      startColor: "#4A9EFF", // brighter tableau blue
      endColor: "#2DDAFF", // brighter tableau cyan
      midPoints: [
        "#FF9F3E", // brighter tableau orange
        "#4CC04C", // brighter tableau green
        "#FF4747", // brighter tableau red
        "#B687ED", // brighter tableau purple
        "#AC767B", // brighter tableau brown
        "#F397F2", // brighter tableau pink
        "#9F9F9F", // brighter tableau gray
        "#DCDD42", // brighter tableau olive
      ],
      colorSpace: "lab",
      description: "Enhanced categorical colors for dark mode",
    },
  },

  [RampTheme.research]: {
    name: "Research",
    description: "Academic and scientific categorical palette",
    light: {
      startColor: "#2E86AB", // academic blue
      endColor: "#A23B72", // academic burgundy
      midPoints: [
        "#F18F01", // academic orange
        "#C73E1D", // academic red
        "#54C6EB", // academic light blue
        "#92374D", // academic maroon
        "#4A7C59", // academic green
        "#F4D35E", // academic yellow
        "#6A994E", // academic olive
      ],
      colorSpace: "lab",
      description: "Academic research visualization colors",
    },
    dark: {
      startColor: "#4EA6DB", // brighter academic blue
      endColor: "#C25BA2", // brighter academic burgundy
      midPoints: [
        "#FFB331", // brighter academic orange
        "#E7634D", // brighter academic red
        "#74D6FB", // brighter academic light blue
        "#B2577D", // brighter academic maroon
        "#6A9C89", // brighter academic green
        "#FFE38E", // brighter academic yellow
        "#8AB97E", // brighter academic olive
      ],
      colorSpace: "lab",
      description: "Enhanced research colors for dark mode",
    },
  },

  [RampTheme.presentation]: {
    name: "Presentation",
    description: "High-contrast palette optimized for presentations",
    light: {
      startColor: "#000000", // black
      endColor: "#FF6B35", // bright orange
      midPoints: [
        "#004E89", // navy blue
        "#1A659E", // blue
        "#F6AE2D", // yellow
        "#FF6B35", // orange
        "#E63946", // red
        "#6A994E", // green
        "#9D4EDD", // purple
      ],
      colorSpace: "lab",
      description: "High-contrast presentation colors",
    },
    dark: {
      startColor: "#FFFFFF", // white
      endColor: "#FF8F65", // soft bright orange
      midPoints: [
        "#5DADE2", // light blue
        "#58D68D", // light green
        "#F7DC6F", // light yellow
        "#FF8F65", // light orange
        "#F1948A", // light red
        "#BB8FCE", // light purple
        "#85C1E9", // very light blue
      ],
      colorSpace: "lab",
      description: "Enhanced presentation colors for dark mode",
    },
  },

  // Data Visualization (Scientific & Statistical)
  [RampTheme.scientific]: {
    name: "Scientific",
    description: "Enhanced scientific palette with categorical distinction",
    light: {
      startColor: "#4C1D95", // deep purple
      endColor: "#CA8A04", // golden yellow
      midPoints: [
        "#7C3AED", // purple
        "#2563EB", // blue
        "#059669", // emerald
        "#CA8A04", // amber
        "#DC2626", // red
        "#EC4899", // pink
        "#0891B2", // cyan
      ],
      colorSpace: "lab",
      description: "Scientific visualization with categorical support",
    },
    dark: {
      startColor: "#581C87", // dark purple
      endColor: "#FBBF24", // bright yellow
      midPoints: [
        "#8B5CF6", // bright purple
        "#3B82F6", // bright blue
        "#10B981", // bright emerald
        "#F59E0B", // bright amber
        "#EF4444", // bright red
        "#F472B6", // bright pink
        "#06B6D4", // bright cyan
      ],
      colorSpace: "lab",
      description: "Enhanced scientific colors for dark mode",
    },
  },

  [RampTheme.statistical]: {
    name: "Statistical",
    description: "Professional statistical visualization palette",
    light: {
      startColor: "#1F2937", // charcoal
      endColor: "#3B82F6", // blue
      midPoints: [
        "#059669", // green
        "#D97706", // amber
        "#DC2626", // red
        "#7C3AED", // purple
        "#0891B2", // cyan
        "#6B7280", // gray
        "#DB2777", // pink
      ],
      colorSpace: "lab",
      description: "Professional statistical colors",
    },
    dark: {
      startColor: "#F3F4F6", // light gray
      endColor: "#60A5FA", // lighter blue
      midPoints: [
        "#10B981", // brighter green
        "#F59E0B", // brighter amber
        "#EF4444", // brighter red
        "#8B5CF6", // brighter purple
        "#06B6D4", // brighter cyan
        "#9CA3AF", // brighter gray
        "#EC4899", // brighter pink
      ],
      colorSpace: "lab",
      description: "Enhanced statistical colors for dark mode",
    },
  },

  // Data Visualization (Sequential with Categorical Support)
  [RampTheme.sequential_blue]: {
    name: "Sequential Blue",
    description: "Blue progression with categorical stops for analytics",
    light: {
      startColor: "#EBF3FF", // very light blue
      endColor: "#0B4F9A", // deep blue
      midPoints: [
        "#3B82F6", // bright blue
        "#1E40AF", // medium blue
        "#1E3A8A", // deep blue
        "#06B6D4", // cyan accent
        "#8B5CF6", // purple accent
        "#10B981", // green accent
        "#F59E0B", // amber accent
      ],
      colorSpace: "lab",
      description: "Blue sequential with categorical accents",
    },
    dark: {
      startColor: "#1E3A8A", // deep blue
      endColor: "#EBF3FF", // light blue
      midPoints: [
        "#3B82F6", // bright blue
        "#60A5FA", // lighter blue
        "#93C5FD", // very light blue
        "#22D3EE", // bright cyan
        "#A78BFA", // bright purple
        "#34D399", // bright green
        "#FBBF24", // bright amber
      ],
      colorSpace: "lab",
      description: "Enhanced blue sequential for dark mode",
    },
  },

  [RampTheme.sequential_multi]: {
    name: "Multi-hue Sequential",
    description: "Progressive multi-hue palette with categorical distinction",
    light: {
      startColor: "#FEF3C7", // warm light
      endColor: "#1E3A8A", // cool dark
      midPoints: [
        "#F59E0B", // amber
        "#EF4444", // red
        "#8B5CF6", // purple
        "#3B82F6", // blue
        "#10B981", // green
        "#06B6D4", // cyan
        "#EC4899", // pink
      ],
      colorSpace: "lab",
      description: "Progressive multi-hue with categorical stops",
    },
    dark: {
      startColor: "#1E3A8A", // cool dark
      endColor: "#FEF3C7", // warm light
      midPoints: [
        "#3B82F6", // blue
        "#8B5CF6", // purple
        "#EC4899", // pink
        "#EF4444", // red
        "#F59E0B", // amber
        "#10B981", // green
        "#06B6D4", // cyan
      ],
      colorSpace: "lab",
      description: "Enhanced multi-hue for dark mode",
    },
  },

  // Data Visualization (Diverging with Categorical Enhancement)
  [RampTheme.diverging_rb]: {
    name: "Red-Blue Diverging",
    description: "Enhanced Red-Blue diverging with categorical accents",
    light: {
      startColor: "#DC2626", // strong red
      endColor: "#2563EB", // strong blue
      midPoints: [
        "#F97316", // orange
        "#EAB308", // yellow
        "#9CA3AF", // neutral gray
        "#10B981", // green
        "#8B5CF6", // purple
        "#EC4899", // pink
        "#06B6D4", // cyan
      ],
      colorSpace: "lab",
      description: "Red-Blue diverging with categorical support",
    },
    dark: {
      startColor: "#EF4444", // bright red
      endColor: "#3B82F6", // bright blue
      midPoints: [
        "#F97316", // bright orange
        "#EAB308", // bright yellow
        "#D1D5DB", // light gray
        "#10B981", // bright green
        "#A78BFA", // bright purple
        "#F472B6", // bright pink
        "#22D3EE", // bright cyan
      ],
      colorSpace: "lab",
      description: "Enhanced Red-Blue diverging for dark mode",
    },
  },

  [RampTheme.diverging_bg]: {
    name: "Brown-Green Diverging",
    description: "Enhanced Brown-Green diverging with natural accents",
    light: {
      startColor: "#92400E", // dark brown
      endColor: "#047857", // dark green
      midPoints: [
        "#D97706", // amber
        "#CA8A04", // yellow
        "#6B7280", // neutral gray
        "#059669", // emerald
        "#0891B2", // teal
        "#7C3AED", // purple
        "#BE185D", // rose
      ],
      colorSpace: "lab",
      description: "Brown-Green diverging with natural categorical accents",
    },
    dark: {
      startColor: "#F59E0B", // bright amber
      endColor: "#10B981", // bright green
      midPoints: [
        "#FBBF24", // bright yellow
        "#EAB308", // yellow
        "#9CA3AF", // gray
        "#34D399", // bright emerald
        "#06B6D4", // bright teal
        "#8B5CF6", // bright purple
        "#EC4899", // bright rose
      ],
      colorSpace: "lab",
      description: "Enhanced Brown-Green diverging for dark mode",
    },
  },

  // Business & Finance
  [RampTheme.financial]: {
    name: "Financial",
    description: "Professional financial visualization palette",
    light: {
      startColor: "#1E3A8A", // deep blue
      endColor: "#B91C1C", // financial red
      midPoints: [
        "#059669", // growth green
        "#D97706", // warning amber
        "#7C2D12", // brown
        "#831843", // wine
        "#374151", // charcoal
        "#6366F1", // indigo
      ],
      colorSpace: "lab",
      description: "Professional financial colors",
    },
    dark: {
      startColor: "#3B82F6", // brighter blue
      endColor: "#EF4444", // brighter red
      midPoints: [
        "#10B981", // brighter growth green
        "#F59E0B", // brighter amber
        "#A3662C", // brighter brown
        "#BE185D", // brighter wine
        "#6B7280", // brighter charcoal
        "#8B5CF6", // brighter indigo
      ],
      colorSpace: "lab",
      description: "Enhanced financial colors for dark mode",
    },
  },

  [RampTheme.enterprise]: {
    name: "Enterprise",
    description: "Professional enterprise-grade color system",
    light: {
      startColor: "#0369A1", // professional blue
      endColor: "#DC2626", // corporate red
      midPoints: [
        "#059669", // professional green
        "#D97706", // amber
        "#7C3AED", // professional purple
        "#DB2777", // magenta
        "#0891B2", // cyan
        "#6B7280", // neutral gray
        "#DC2626", // red
      ],
      colorSpace: "lab",
      description: "Corporate-grade visualization colors",
    },
    dark: {
      startColor: "#0EA5E9", // brighter blue for dark
      endColor: "#EF4444", // brighter red for dark
      midPoints: [
        "#10B981", // brighter green
        "#F59E0B", // brighter amber
        "#8B5CF6", // brighter purple
        "#EC4899", // brighter magenta
        "#06B6D4", // brighter cyan
        "#9CA3AF", // brighter gray
        "#EF4444", // brighter red
      ],
      colorSpace: "lab",
      description: "Enhanced enterprise colors for dark mode",
    },
  },

  // Nature Inspired (Enhanced for Analytics)
  [RampTheme.earth]: {
    name: "Earth",
    description: "Natural earth tones with categorical accents",
    light: {
      startColor: "#8B4513", // saddle brown
      endColor: "#F4A460", // sandy brown
      midPoints: [
        "#D2691E", // chocolate
        "#CD853F", // peru
        "#DEB887", // burlywood
        "#F4A460", // sandy brown
        "#B22222", // fire brick (red earth)
        "#228B22", // forest green (vegetation)
        "#4682B4", // steel blue (water/sky)
      ],
      colorSpace: "oklch",
      description: "Earth tones with natural categorical accents",
    },
    dark: {
      startColor: "#A0522D", // sienna
      endColor: "#F5DEB3", // wheat
      midPoints: [
        "#CD853F", // peru
        "#DEB887", // burlywood
        "#F4A460", // sandy brown
        "#F5DEB3", // wheat
        "#DC143C", // crimson (red earth)
        "#32CD32", // lime green (vegetation)
        "#87CEEB", // sky blue (water/sky)
      ],
      colorSpace: "oklch",
      description: "Enhanced earth tones for dark mode",
    },
  },

  [RampTheme.forest]: {
    name: "Forest",
    description: "Forest greens with natural categorical accents",
    light: {
      startColor: "#2E7D32", // forest green
      endColor: "#8BC34A", // light green
      midPoints: [
        "#4CAF50", // green
        "#66BB6A", // light green
        "#81C784", // very light green
        "#A5D6A7", // pale green
        "#8D6E63", // tree bark brown
        "#FF9800", // autumn orange
        "#2196F3", // sky blue
      ],
      colorSpace: "oklch",
      description: "Forest greens with natural categorical accents",
    },
    dark: {
      startColor: "#4CAF50", // bright green
      endColor: "#C8E6C9", // pale green
      midPoints: [
        "#66BB6A", // light green
        "#81C784", // very light green
        "#A5D6A7", // pale green
        "#C8E6C9", // very pale green
        "#BCAAA4", // light bark brown
        "#FFB74D", // bright autumn orange
        "#64B5F6", // bright sky blue
      ],
      colorSpace: "oklch",
      description: "Enhanced forest colors for dark mode",
    },
  },

  [RampTheme.ocean]: {
    name: "Ocean",
    description: "Ocean blues with marine categorical accents",
    light: {
      startColor: "#0277BD", // deep ocean blue
      endColor: "#81D4FA", // light blue
      midPoints: [
        "#0288D1", // ocean blue
        "#039BE5", // blue
        "#03A9F4", // lighter blue
        "#29B6F6", // bright blue
        "#4CAF50", // sea green (seaweed)
        "#FF9800", // sunset orange
        "#8D6E63", // driftwood brown
      ],
      colorSpace: "oklch",
      description: "Ocean blues with marine categorical accents",
    },
    dark: {
      startColor: "#03A9F4", // bright blue
      endColor: "#E1F5FE", // very light blue
      midPoints: [
        "#29B6F6", // bright blue
        "#4FC3F7", // sky blue
        "#81D4FA", // light blue
        "#B3E5FC", // very light blue
        "#81C784", // bright sea green
        "#FFB74D", // bright sunset orange
        "#BCAAA4", // light driftwood brown
      ],
      colorSpace: "oklch",
      description: "Enhanced ocean colors for dark mode",
    },
  },

  // UI Progressions
  [RampTheme.neutral]: {
    name: "Neutral",
    description: "Professional and balanced progression",
    light: {
      startColor: "#F5F5F5",
      endColor: "#212121",
      midPoints: [
        "#EEEEEE", // very light gray
        "#E0E0E0", // light gray
        "#BDBDBD", // medium light gray
        "#9E9E9E", // medium gray
        "#757575", // dark gray
        "#616161", // darker gray
        "#424242", // very dark gray
      ],
      colorSpace: "oklch",
      description: "Clean neutral progression",
    },
    dark: {
      startColor: "#212121",
      endColor: "#FAFAFA",
      midPoints: [
        "#424242", // very dark gray
        "#616161", // darker gray
        "#757575", // dark gray
        "#9E9E9E", // medium gray
        "#BDBDBD", // medium light gray
        "#E0E0E0", // light gray
        "#EEEEEE", // very light gray
      ],
      colorSpace: "oklch",
      description: "Rich neutral progression",
    },
  },

  [RampTheme.high_contrast]: {
    name: "High Contrast",
    description: "Maximum contrast for accessibility",
    light: {
      startColor: "#FFFFFF",
      endColor: "#000000",
      midPoints: [
        "#FF0000", // red
        "#00FF00", // green
        "#0000FF", // blue
        "#FFFF00", // yellow
        "#FF00FF", // magenta
        "#00FFFF", // cyan
        "#808080", // gray
      ],
      colorSpace: "lab",
      description: "Maximum contrast progression",
    },
    dark: {
      startColor: "#000000",
      endColor: "#FFFFFF",
      midPoints: [
        "#800000", // dark red
        "#008000", // dark green
        "#000080", // dark blue
        "#808000", // dark yellow
        "#800080", // dark magenta
        "#008080", // dark cyan
        "#808080", // gray
      ],
      colorSpace: "lab",
      description: "Inverted contrast progression",
    },
  },

  [RampTheme.modern]: {
    name: "Modern",
    description: "Clean, professional palette with subtle depth",
    light: {
      startColor: "#E8EAF6",
      endColor: "#283593",
      midPoints: [
        "#C5CAE9", // very light purple
        "#9FA8DA", // light purple
        "#7986CB", // medium purple
        "#5C6BC0", // bright purple
        "#3F51B5", // strong purple
        "#3949AB", // deep purple
        "#303F9F", // dark purple
      ],
      colorSpace: "oklch",
      description: "Clean and professional progression",
    },
    dark: {
      startColor: "#1A237E",
      endColor: "#E8EAF6",
      midPoints: [
        "#283593", // dark purple
        "#303F9F", // deep purple
        "#3949AB", // strong purple
        "#3F51B5", // bright purple
        "#5C6BC0", // medium purple
        "#7986CB", // light purple
        "#9FA8DA", // very light purple
      ],
      colorSpace: "oklch",
      description: "Deep, rich progression for dark mode",
    },
  },
};

// Color modification utilities
const colorTransforms = {
  soften: (color: string, amount: number = 0.2) =>
    chroma(color).desaturate(amount).hex(),

  saturate: (color: string, amount: number = 0.2) =>
    chroma(color).saturate(amount).hex(),

  shift: (color: string, hueShift: number) => {
    const col = chroma(color);
    const [h, s, l] = col.hsl();
    return chroma.hsl((h + hueShift) % 360, s, l).hex();
  },
};

// Add new color generation methods
const colorGenerators = {
  // Generate a bezier interpolation through multiple colors
  bezier: (colors: string[], total: number): string[] =>
    chroma.bezier(colors).scale().correctLightness().colors(total),

  // Generate complementary colors
  complement: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [base.hex(), chroma.hsl((h + 180) % 360, s, l).hex()];
  },

  // Generate triadic colors
  triadic: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 120) % 360, s, l).hex(),
      chroma.hsl((h + 240) % 360, s, l).hex(),
    ];
  },

  // Generate split complementary colors
  splitComplement: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 150) % 360, s, l).hex(),
      chroma.hsl((h + 210) % 360, s, l).hex(),
    ];
  },

  // Generate analogous colors
  analogous: (baseColor: string, count: number = 3): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    const step = 30;
    return Array.from({ length: count }, (_, i) => {
      const shift = (i - Math.floor(count / 2)) * step;
      return chroma.hsl((360 + h + shift) % 360, s, l).hex();
    });
  },

  // Generate a more sophisticated monochromatic scale
  advancedMonotone: (baseColor: string, total: number): string[] => {
    const base = chroma(baseColor);
    return chroma
      .scale([base.luminance(0.9), base, base.luminance(0.1)])
      .mode("lch")
      .correctLightness()
      .colors(total);
  },

  // New advanced generators
  tetradic: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return [
      base.hex(),
      chroma.hsl((h + 90) % 360, s, l).hex(),
      chroma.hsl((h + 180) % 360, s, l).hex(),
      chroma.hsl((h + 270) % 360, s, l).hex(),
    ];
  },

  qualitative: (count: number = 8): string[] => {
    // Optimized for data visualization
    const palette = [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
      "#bcbd22",
      "#17becf",
    ];
    return palette.slice(0, count);
  },

  sequential: (baseColor: string, count: number = 5): string[] => {
    const base = chroma(baseColor);
    return chroma
      .scale([
        base.luminance(0.95),
        base.luminance(0.75),
        base,
        base.luminance(0.25),
        base.luminance(0.05),
      ])
      .mode("lab")
      .correctLightness()
      .colors(count);
  },

  diverging: (color1: string, color2: string, count: number = 7): string[] => {
    return chroma
      .scale([color1, "#f7f7f7", color2])
      .mode("lab")
      .correctLightness()
      .colors(count);
  },

  accent: (baseColor: string, count: number = 3): string[] => {
    const base = chroma(baseColor);
    const [h, s, l] = base.hsl();
    return Array.from({ length: count }, (_, i) => {
      const hueShift = (i * 137.5) % 360;
      const satMod = 0.1 * Math.sin((i * Math.PI) / count);
      return chroma
        .hsl((h + hueShift) % 360, Math.min(1, Math.max(0.2, s + satMod)), l)
        .hex();
    });
  },

  materialDesign: (baseColor: string): string[] => {
    const base = chroma(baseColor);
    return [50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map((weight) => {
      const factor = (weight - 500) / 400;
      return base
        .set("lab.l", "*" + (1 + factor * 0.4))
        .saturate(factor * 0.2)
        .hex();
    });
  },
};

// Add color blend modes
const blendModes = {
  multiply: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "multiply").hex(),

  screen: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "screen").hex(),

  overlay: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "overlay").hex(),

  darken: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "darken").hex(),

  lighten: (color1: string, color2: string) =>
    chroma.blend(color1, color2, "lighten").hex(),
};

// Maintain existing exports with new implementation
export function singleColor() {
  return "#4E5BA6";
}

export function monoColors() {
  return generateMonotone("#4E5BA6", 5);
}

export function successColors(token?: GlobalToken) {
  if (token) {
    return [
      token.green4,
      token.green5,
      token.green6,
      token.green7,
      token.green8,
    ];
  }
  return generateMonotone("#24A148", 5); // IBM Forest Green
}

export function errorColors(token?: GlobalToken) {
  if (token) {
    return [token.red4, token.red5, token.red6, token.red7, token.red8];
  }
  return generateMonotone("#DA1E28", 5); // IBM Cardinal Red
}

export function fullSpectrumColors() {
  return [
    "#3E4784",
    "#717BBC",
    "#B3B8DB",
    "#3F621A",
    "#669F2A",
    "#ACDC79",
    "#107569",
    "#15B79E",
    "#5FE9D0",
    "#0E6F90",
    "#06AED4",
    "#67E3F9",
    "#C01048",
    "#F63D68",
    "#FEA3B4",
  ];
}

// Core color generation function
function generateMonotone(baseColor: string, total: number): string[] {
  return chroma
    .scale([
      chroma(baseColor).brighten(2),
      baseColor,
      chroma(baseColor).darken(2),
    ])
    .mode("oklch")
    .colors(total);
}

// Main theme color generator
export function themeColors(
  theme: RampTheme,
  mode: "dark" | "light",
  isMonotone: boolean,
  isMuted: boolean,
  isPastel: boolean,
  hueReverse: boolean,
  hueShift: number,
  total: number,
  hCycles?: number
): string[] {
  const themeConfig = RAMPS[theme] || RAMPS[RampTheme.classic];
  const config = themeConfig[mode];

  let colors: string[];

  if (isMonotone) {
    colors = colorGenerators.advancedMonotone(config.startColor, total);
  } else {
    const colorPoints = config.midPoints
      ? [config.startColor, ...config.midPoints, config.endColor]
      : [config.startColor, config.endColor];

    // Use bezier interpolation for smoother transitions
    colors = colorGenerators.bezier(colorPoints, total);
  }

  // Apply modifications with improved handling
  if (isMuted) {
    colors = colors.map((c) =>
      chroma(c)
        .desaturate(1)
        .luminance(chroma(c).luminance() * 1.05) // Slight lightness compensation
        .hex()
    );
  }

  if (isPastel) {
    colors = colors.map((c) =>
      chroma(c)
        .luminance(0.8) // More consistent pastel effect
        .saturate(0.1)
        .hex()
    );
  }

  if (hueShift !== 0) {
    colors = colors.map((c) => colorTransforms.shift(c, hueShift));
  }

  if (hueReverse) {
    colors.reverse();
  }

  return colors;
}

// Utility functions
export function hslToHex(h: number, s: number, l: number): string {
  return chroma.hsl(h, s / 100, l / 100).hex();
}

export function hexToRgb(hex: string): [number, number, number] {
  if (hex === undefined) return [255, 255, 255];
  const [r, g, b] = chroma(hex).rgb();
  return [r, g, b];
}

export function rgbToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  const [h, s, l] = chroma.rgb(r, g, b).hsl();
  return [h, s * 100, l * 100];
}

export function getContrastTextColor(backgroundColor: string): string {
  // First check if it's a very dark color using luminance
  const luminance = chroma(backgroundColor).luminance();
  if (luminance < 0.15) {
    // Very dark colors should always get white text
    return "#ffffff";
  }

  // For other colors, use contrast ratio with a higher threshold
  return chroma.contrast(backgroundColor, "white") >= 3.5
    ? "#ffffff"
    : "#000000";
}

// Ensure backward compatibility
export const getLuminance = (r: number, g: number, b: number): number => {
  return chroma.rgb(r, g, b).luminance();
};

// Add new export for advanced color generation
export function generateColorScheme(
  baseColor: string,
  scheme: ColorSchemeType,
  options: ColorSchemeOptions = {}
): string[] {
  const {
    count = 5,
    saturation = 1,
    lightness = 0.5,
    rotation = 0,
    spread = 30,
  } = options;

  switch (scheme) {
    case "complement": {
      return colorGenerators.complement(baseColor);
    }
    case "triadic": {
      return colorGenerators.triadic(baseColor);
    }
    case "splitComplement": {
      return colorGenerators.splitComplement(baseColor);
    }
    case "analogous": {
      return colorGenerators.analogous(baseColor, count);
    }
    case "tetradic": {
      return colorGenerators.tetradic(baseColor);
    }
    case "square": {
      return colorGenerators.tetradic(baseColor); // Same as tetradic but with equal spacing
    }
    case "accent": {
      return colorGenerators.accent(baseColor, count);
    }
    case "qualitative": {
      return colorGenerators.qualitative(count);
    }
    case "sequential": {
      return colorGenerators.sequential(baseColor, count);
    }
    case "diverging": {
      const complement = colorGenerators.complement(baseColor)[1];
      return colorGenerators.diverging(baseColor, complement, count);
    }
    default: {
      return [baseColor];
    }
  }
}

// Add new exports for advanced color operations
export function generateMaterialPalette(baseColor: string): string[] {
  return colorGenerators.materialDesign(baseColor);
}

export function blendColors(
  color1: string,
  color2: string,
  mode: keyof typeof blendModes
): string {
  return blendModes[mode](color1, color2);
}

// Improved hash function with better distribution
function improvedHash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.codePointAt(i)!;
  }
  return Math.abs(hash);
}

// Calculate perceptual color distance (Delta E) in LAB space
function getColorDistance(color1: string, color2: string): number {
  const lab1 = chroma(color1).lab();
  const lab2 = chroma(color2).lab();

  const deltaL = lab1[0] - lab2[0];
  const deltaA = lab1[1] - lab2[1];
  const deltaB = lab1[2] - lab2[2];

  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
}

// Consistent color assignment for data groups
export function getConsistentColor(
  groupName: string,
  theme: RampTheme = RampTheme.classic,
  mode: "dark" | "light" = "light"
): string {
  // Get theme-respecting palette
  const palette = getOptimalPalette(theme, mode, 12);

  // Use improved hash to consistently map group name to color index
  const hash = improvedHash(groupName);
  const colorIndex = hash % palette.length;

  return palette[colorIndex];
}

// Get optimal color palette that respects theme selection
function getOptimalPalette(
  theme: RampTheme,
  mode: "dark" | "light" = "light",
  maxColors: number = 12
): string[] {
  const themeConfig = RAMPS[theme] || RAMPS[RampTheme.classic];
  const config = themeConfig[mode];

  // Build categorical palette from theme's predefined colors
  const themeColors = [];

  // Start with theme's start color
  themeColors.push(config.startColor);

  // Add all midPoints (these are specifically chosen for distinction)
  if (config.midPoints) {
    themeColors.push(...config.midPoints);
  }

  // Add end color
  themeColors.push(config.endColor);

  // If we have enough colors from the theme, use them directly
  if (maxColors <= themeColors.length) {
    return themeColors.slice(0, maxColors);
  }

  // If we need more colors, use smart interpolation within theme's color space
  return generateExtendedThemePalette(theme, mode, maxColors);
}

// Generate extended palette using theme's color space and harmony
function generateExtendedThemePalette(
  theme: RampTheme,
  mode: "dark" | "light",
  count: number
): string[] {
  const themeConfig = RAMPS[theme] || RAMPS[RampTheme.classic];
  const config = themeConfig[mode];

  // Get base colors from theme
  const baseColors = [config.startColor];
  if (config.midPoints) {
    baseColors.push(...config.midPoints);
  }
  baseColors.push(config.endColor);

  // If we need fewer colors than available, return subset
  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }

  // For more colors, use golden ratio based hue distribution within theme's color range
  return generateGoldenRatioExtension(baseColors, count, config.colorSpace);
}

// Use golden ratio for perceptually uniform color distribution
function generateGoldenRatioExtension(
  baseColors: string[],
  targetCount: number,
  colorSpace: string
): string[] {
  if (targetCount <= baseColors.length) {
    return baseColors.slice(0, targetCount);
  }

  const result = [...baseColors];
  const needed = targetCount - baseColors.length;

  // Use chroma to create additional colors that harmonize with the base theme
  const colorRange = chroma
    .scale(baseColors)
    .mode(colorSpace as any)
    .correctLightness();

  // Generate additional colors using golden ratio distribution
  const goldenRatio = 1.618_033_988_749;
  for (let i = 0; i < needed; i++) {
    const position = (i * goldenRatio) % 1;
    result.push(colorRange(position).hex());
  }

  return result.slice(0, targetCount);
}

// Advanced color assignment with conflict resolution
function assignColorsWithDistinction(
  groupNames: string[],
  availableColors: string[],
  minDistance: number = 30
): Record<string, string> {
  const palette: Record<string, string> = {};
  const usedColors: string[] = [];
  const sortedNames = [...groupNames].sort();

  for (const name of sortedNames) {
    let bestColor = availableColors[0];
    let bestDistance = 0;

    // Try to find the most distinct color from already used colors
    for (const color of availableColors) {
      if (usedColors.length === 0) {
        bestColor = color;
        break;
      }

      const minDistanceToUsed = Math.min(
        ...usedColors.map((used) => getColorDistance(color, used))
      );

      if (minDistanceToUsed > bestDistance) {
        bestDistance = minDistanceToUsed;
        bestColor = color;
      }
    }

    palette[name] = bestColor;
    usedColors.push(bestColor);

    // If we've used all available colors, start cycling with hash-based selection
    if (usedColors.length >= availableColors.length) {
      const hash = improvedHash(name);
      const colorIndex = hash % availableColors.length;
      palette[name] = availableColors[colorIndex];
    }
  }

  return palette;
}

// Enhanced version that supports multiple group types
export function getConsistentColorPalette(
  groupNames: string[],
  theme: RampTheme = RampTheme.classic,
  mode: "dark" | "light" = "light"
): Record<string, string> {
  if (groupNames.length === 0) return {};

  // Get theme-respecting color palette
  const availableColors = getOptimalPalette(
    theme,
    mode,
    Math.max(12, groupNames.length)
  );

  // For small numbers of groups, use advanced assignment for maximum distinction
  if (groupNames.length <= availableColors.length) {
    return assignColorsWithDistinction(groupNames, availableColors);
  }

  // For large numbers of groups, fall back to hash-based assignment
  const palette: Record<string, string> = {};
  const sortedNames = [...groupNames].sort();

  sortedNames.forEach((name) => {
    const hash = improvedHash(name);
    const colorIndex = hash % availableColors.length;
    palette[name] = availableColors[colorIndex];
  });

  return palette;
}

// Add a new function for advanced color manipulation
export function manipulateColor(
  color: string,
  operations: {
    saturate?: number;
    desaturate?: number;
    lighten?: number;
    darken?: number;
    brighten?: number;
    rotate?: number;
  }
): string {
  let result = chroma(color);

  if (operations.saturate) result = result.saturate(operations.saturate);
  if (operations.desaturate) result = result.desaturate(operations.desaturate);
  if (operations.lighten) result = result.brighten(operations.lighten);
  if (operations.darken) result = result.darken(operations.darken);
  if (operations.brighten) result = result.brighten(operations.brighten);
  if (operations.rotate) {
    const [h, s, l] = result.hsl();
    result = chroma.hsl((h + operations.rotate) % 360, s, l);
  }

  return result.hex();
}

export function generateGradient(
  startColor: string,
  endColor: string,
  steps: number
): string[] {
  return chroma.scale([startColor, endColor]).mode("lch").colors(steps);
}

export function getContrastColor(
  backgroundColor: string,
  token?: GlobalToken
): string {
  if (token) {
    const lightContrast = chroma.contrast(
      backgroundColor,
      token.colorTextLightSolid
    );
    const darkContrast = chroma.contrast(backgroundColor, token.colorText);
    return lightContrast > darkContrast
      ? token.colorTextLightSolid
      : token.colorText;
  }
  const contrast = chroma.contrast(backgroundColor, "#ffffff");
  return contrast > 4.5 ? "#ffffff" : "#000000";
}
